import URL from '@/creditflowresearch/Router/urlConstants';
import { getParentRoute } from '@/creditflowresearch/Router/RouteWithRedirect/getNewRoute';
import Product, { ProductAbbreviations, ProductType } from '@/creditflowresearch/domain/Product';
import { NAV_ITEMS_TREE } from '@/creditflowresearch/ui/common/constants/panelData';
import { getMarketOverviewUrl } from '@/creditflowresearch/domain/Product/helpers/getMarketOverviewUrl';
import { hasEmMarket } from '@/creditflowresearch/domain/Market/selectors/hasEmMarket';

interface IMarketOverviewFilters {
  products?: string | string[],
  regions?: string | string[],
  markets?: string | string[],
  segments?: string | string[],
}

type TFilterKey = keyof IMarketOverviewFilters;

interface IMarketOverviewBreadcrumb {
  title: string,
  url: string,
  overrides?: Partial<IMarketOverviewFilters>,
}

interface IMarketOverviewRouteConfig {
  matches: (filters: IMarketOverviewFilters) => boolean,
  filterKeys: TFilterKey[],
  buildBreadcrumb: (filters: IMarketOverviewFilters) => IMarketOverviewBreadcrumb,
}

const DEFAULT_BREADCRUMB_TITLE = 'Market Overview';

export const getPageTitle = (title: string): string => {
  const company = 'FINSIGHT';

  if (!title || title.includes(company)) {
    return title || company;
  }

  return `${ title } | ${ company }`;
};

export const getRatingsMapping = (tranches, ratingAgenciesForRegion) => {
  let agenciesForDisplay = {};

  ratingAgenciesForRegion.forEach((agency) => {
    if (agency.isAlwaysDisplay) {
      agenciesForDisplay[agency.id] = agency;
    }
  });

  tranches.forEach((tranche) => {
    Object.keys(tranche.ratings).forEach((ratingAgencyFromTranche) => {
      let ratingAgency = ratingAgenciesForRegion
        .find((ratingAgencyForRegion) => ratingAgencyForRegion.id === ratingAgencyFromTranche);
      if (ratingAgency?.id) {
        agenciesForDisplay[ratingAgency.id] = ratingAgency;
      }
    });
  });

  return Object
    .values(agenciesForDisplay)
      // @ts-ignore
    .sort((a, b) => a.priority - b.priority)
    .slice(0, 6);
};

const normalizeToArray = (value?: string | string[]): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return value.split(',').map((item) => item.trim());
};

const normalizeToString = (value?: string | string[]): string => {
  if (!value) return '';
  if (Array.isArray(value)) return value.join(',');
  return value;
};

const MARKET_OVERVIEW_BREADCRUMB_ROUTES: IMarketOverviewRouteConfig[] = [
  {
    matches: ({ products }) => {
      const productsArray = normalizeToArray(products);
      return productsArray.length > 0 && !Product.hasAnyOfType(productsArray, ProductType.ABS);
    },
    filterKeys: ['products', 'regions', 'markets', 'segments'],
    buildBreadcrumb: (filters) => {
      const products = normalizeToArray(filters.products);
      const primaryProductAbbr = products[0] as ProductAbbreviations;

      return {
        title: NAV_ITEMS_TREE.find((item) => item.abbreviation === primaryProductAbbr)?.label ?? DEFAULT_BREADCRUMB_TITLE,
        url: getMarketOverviewUrl(Product.getId(primaryProductAbbr)) ?? URL.MARKET_OVERVIEW_HYC,
        overrides: { products: products.join(',') },
      };
    },
  },
  {
    matches: ({ markets }) => hasEmMarket({ markets }),
    filterKeys: ['regions', 'markets', 'segments'],
    buildBreadcrumb: () => ({
      title: 'Market Overview',
      url: URL.MARKET_OVERVIEW_EM,
    }),
  },
  {
    matches: () => true,
    filterKeys: ['products', 'regions'],
    buildBreadcrumb: (filters) => ({
      title: NAV_ITEMS_TREE.find((item) => item.abbreviation === ProductAbbreviations.ABS)?.label ?? DEFAULT_BREADCRUMB_TITLE,
      url: URL.MARKET_OVERVIEW_ALL,
      overrides: filters.products ? { products: ProductAbbreviations.ABS } : {},
    }),
  },
];

export const getMarketOverviewBreadcrumbsRoute = (filters: IMarketOverviewFilters = {}, withParentLink = false) => {
  const route = MARKET_OVERVIEW_BREADCRUMB_ROUTES.find(({ matches }) => matches(filters));
  const { title, url, overrides } = route.buildBreadcrumb(filters);
  const mergedFilters = { ...filters, ...overrides };

  const queryParams = route.filterKeys
    .filter((key) => mergedFilters[key])
    .map((key) => `${ key }=${ encodeURI(normalizeToString(mergedFilters[key])) }`);

  const queryString = queryParams.length > 0 ? `?${ queryParams.join('&') }` : '';

  return {
    title,
    url: `${ url }${ queryString }`,
    withParentLink,
  };
};

export const getParentBreadcrumbsRoute = (parent) => ({
  title: parent.name,
  url: getParentRoute(parent),
});

export const getFormatUrl = (url: string): string => {
  const [basePath, query] = url.split('?');

  return query ? url : basePath;
};
